<template>
  <div v-if="insertion">
    <HgModal
      v-model="internalModel"
      name="toplistingModal"
      :containerCssClass="$style.modalContainer"
      :class="$style.modalContainerWrapper"
      :fullScreenMobile="true"
      :tabindex="-1"
      @close="onModalClose"
    >
      <template #title>{{ bookingFormTitle }}</template>
      <ToplistingBookingForm
        ref="bookingForm"
        :insertion="insertion"
        :conversionSource="props.conversionSource"
        :active="internalModel"
        @update:title="bookingFormTitle = $event"
        @update:footerBindings="footerBindings = $event"
        @close-request="onBookingFormCloseRequest"
        @deactivate-request="openDeactivateDialog"
      />
      <template #footerAction>
        <HgLink
          v-if="
            footerBindings.toplistingModalAction === ToplistingModalAction.EDIT &&
            !footerBindings.isModalReadOnly
          "
          ref="deactivateToplisting"
          :class="$style.wrapperIcon"
          :disabled="footerBindings.isDeactivateButtonDisabled"
          @click="openDeactivateDialog"
        >
          <HgIcon icon="Delete" size="large" theme="secondary" />
          {{ deactivationLinkText }}
        </HgLink>
      </template>
      <template #footer>
        <HgButton
          type="button"
          theme="primary-bordered"
          :size="ButtonSize.Small"
          @click="onFooterCancel"
        >
          {{
            footerBindings.isModalReadOnly
              ? t('general.buttons.close')
              : t('general.buttons.cancel')
          }}
        </HgButton>
        <HgButton
          v-if="!footerBindings.isSubmitButtonHidden && !footerBindings.isModalReadOnly"
          ref="bookButton"
          :disabled="footerBindings.isPrimaryActionDisabled"
          theme="primary"
          :size="ButtonSize.Small"
          @click="onFooterSubmit"
        >
          <LoadingIndicator
            v-if="footerBindings.submitLoadingIndicator"
            :class="$style.loadingIndicator"
          />
          {{ footerBindings.submitButtonText }}
        </HgButton>
      </template>
    </HgModal>
    <ConfirmationDialog
      ref="deactivationDialog"
      v-model="showDeactivateDialog"
      :buttonStyle="$style.deactivationDialogButtons"
      :confirmText="toplistingDeactivationConfirmText"
      :title="toplistingDeactivationTitle"
      :trackingCancelAction="EventAction.CANCEL_DEACTIVATE_TOPLISTING"
      :trackingCloseAction="EventAction.CANCEL_DEACTIVATE_TOPLISTING"
      :trackingConfirmAction="EventAction.DEACTIVATE_TOPLISTING"
      :trackingListingId="insertion.listing?.id ?? undefined"
      @confirm="confirmToplistingDeactivation"
      @closeDialog="showDeactivateDialog = false"
    >
      {{ toplistingDeactivationBody }}
    </ConfirmationDialog>
  </div>
</template>

<script setup lang="ts">
import { RankType } from '@smg-real-estate/hg-rets-schema';
import { computed, ref, useTemplateRef, watch } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import LoadingIndicator from '@/assets/images/loading_indicator.svg';
import ConfirmationDialog from '@/components/ConfirmationDialog/ConfirmationDialog.vue';
import ToplistingBookingForm, {
  type FooterBindings,
} from '@/components/ToplistingBookingForm/ToplistingBookingForm.vue';
import handleErrorWithNotification from '@/helpers/handleErrorWithNotification';
import { getUbmPriceElasticityExperimentVariantArray } from '@/helpers/toplistingPriceElasticityTracking';
import { ConversionSource, EventAction, EventLabel } from '@/services/tracking/GtmTrackingService';
import {
  trackToplistingDeactivationModalDisplayed,
  trackToplistingInteraction,
  trackToplistingModalDisplayed,
} from '@/services/tracking/Tracking';
import { ToplistingModalAction, type Insertion } from '@/types';
import { ButtonSize, HgButton, HgIcon, HgLink, HgModal } from '@smg-real-estate/domus';

const defaultFooterBindings = (): FooterBindings => ({
  isPrimaryActionDisabled: true,
  submitLoadingIndicator: false,
  submitButtonText: '',
  isSubmitButtonHidden: false,
  isModalReadOnly: false,
  toplistingModalAction: ToplistingModalAction.CREATE,
  isDeactivateButtonDisabled: false,
  isNoPromotionSelected: false,
});

interface Props {
  modelValue: boolean;
  insertion?: Insertion;
  conversionSource?: ConversionSource;
}

const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'closeDialog']);

const store = useStore();
const { t } = useI18n();

const bookingFormComponentRef =
  useTemplateRef<ComponentExposed<typeof ToplistingBookingForm>>('bookingForm');

const bookingFormTitle = ref<string>('');
const showDeactivateDialog = ref<boolean>(false);
const footerBindings = ref<FooterBindings>(defaultFooterBindings());

const toplistingExperimentVariantArray = computed<string | undefined>(() =>
  getUbmPriceElasticityExperimentVariantArray({
    customerId: store.getters.getActiveAgencyId,
    objectReference: props.insertion?.objectReference,
  }),
);

const toplistingDeactivationProductName = computed<string>(() =>
  props.insertion?.toplistingData?.listingType === RankType.Premium
    ? t('toplisting.badge.premium')
    : t('toplisting.badge.top'),
);

const toplistingDeactivationTitle = computed<string>(() =>
  footerBindings.value.deactivationKind === 'regionalHighlight'
    ? t('toplisting.modal.regionalHighlight.deactivate.title')
    : t('toplisting.modal.deactivationExperiment.title', {
        productName: toplistingDeactivationProductName.value,
      }),
);

const toplistingDeactivationBody = computed<string>(() =>
  footerBindings.value.deactivationKind === 'regionalHighlight'
    ? t('toplisting.modal.regionalHighlight.deactivate.body')
    : t('toplisting.modal.deactivationExperiment.body', {
        productName: toplistingDeactivationProductName.value,
      }),
);

const toplistingDeactivationConfirmText = computed<string>(() =>
  footerBindings.value.deactivationKind === 'regionalHighlight'
    ? t('toplisting.modal.regionalHighlight.deactivate.confirm')
    : t('toplisting.modal.deactivationExperiment.confirmText'),
);
const deactivationLinkText = computed<string>(() =>
  footerBindings.value.deactivationKind === 'regionalHighlight'
    ? t('toplisting.modal.regionalHighlight.deactivate.link')
    : t('toplisting.modal.deactivate'),
);

watch(
  () => props.modelValue,
  (isModalOpen) => {
    if (isModalOpen) {
      if (!props.conversionSource) {
        throw new Error('ToplistingModal: conversionSource is required');
      }
      const listingId = props.insertion?.listing?.id ?? undefined;
      trackToplistingModalDisplayed(
        listingId,
        props.conversionSource,
        !listingId ? props.insertion?.objectReference || undefined : undefined,
        toplistingExperimentVariantArray.value,
      );
    }
  },
  { immediate: true },
);

// in case you ever read comments or even code, `internalModel` is the UBM way to toggle modals opened/closed
const internalModel = computed<boolean>({
  get: () => props.modelValue,
  set: (val) => {
    emit('update:modelValue', val);
    if (!val) {
      emit('closeDialog');
    }
  },
});

const onModalClose = (): void => {
  internalModel.value = false;
  const experimentVariantArray = toplistingExperimentVariantArray;
  if (!props.conversionSource) {
    throw new Error('onModalClose: conversionSource is required');
  }

  trackToplistingInteraction({
    action: EventAction.CLOSE_TOPLISTING,
    label: EventLabel.IN_MODAL,
    listingId: props.insertion?.listing?.id ?? undefined,
    objectReferenceId: !props.insertion?.listing?.id
      ? props.insertion?.objectReference || undefined
      : undefined,
    conversionSource: props.conversionSource,
    ...(experimentVariantArray.value !== undefined
      ? { experimentVariantArray: experimentVariantArray.value }
      : {}),
  });
};

const onFooterCancel = (): void => {
  bookingFormComponentRef.value?.cancel();
};

const onFooterSubmit = (): void => {
  bookingFormComponentRef.value?.submitForm();
};

const onBookingFormCloseRequest = (): void => {
  internalModel.value = false;
};
const openDeactivateDialog = (): void => {
  if (!showDeactivateDialog.value) {
    trackToplistingDeactivationModalDisplayed(props.insertion?.listing?.id ?? undefined);
  }
  showDeactivateDialog.value = true;
};

const confirmToplistingDeactivation = async (): Promise<void> => {
  if (footerBindings.value.deactivationKind === 'regionalHighlight') {
    await handleErrorWithNotification(async () => {
      await bookingFormComponentRef.value?.deactivateRegionalHighlight();
      internalModel.value = false;
      showDeactivateDialog.value = false;
    }, store);
    return;
  }

  internalModel.value = false;
  showDeactivateDialog.value = false;

  handleErrorWithNotification(
    () =>
      store.dispatch('deleteToplisting', {
        insertionId: props.insertion?.id,
        objectReferenceId: props.insertion?.objectReference,
      }),
    store,
    { insertionId: props.insertion?.id },
  );
};

defineExpose({
  showDeactivateDialog,
  openDeactivateDialog,
});
</script>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/assets/mixins';

.wrapperIcon {
  @include icon-link-wrapper;
}

.loadingIndicator {
  width: var(--typo-body-3-font-size);
  padding-right: $space-100;
  filter: grayscale(1) brightness(300%);
}

.deactivationDialogButtons {
  margin: $space-50;

  &:nth-child(2) {
    margin-right: 20px;
    color: #fff;
    background-color: $color-danger;
    border-color: $color-danger;

    &:hover,
    &:focus {
      background-color: $color-danger-hover;
      border-color: $color-danger-hover;
    }
  }
}

@media screen and (min-width: $viewport-sm-min) {
  .modalContainerWrapper .modalContainer {
    max-width: 750px;
  }
}
</style>
